import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  AlertTriangle, BarChart3, Bell, BookOpen, Briefcase, Calendar, ClipboardList, GitPullRequest,
  LayoutDashboard, LogOut, Settings, Sparkles, Wrench
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const unread = notifs.filter((n) => !n.is_read).length;

  async function loadNotifs() {
    try { const { data } = await api.get("/api/notifications"); setNotifs(data); } catch {}
  }
  useEffect(() => {
    loadNotifs();
    const i = setInterval(loadNotifs, 60000);
    return () => clearInterval(i);
  }, []);

  async function markAll() {
    await api.put("/api/notifications/mark-all-read");
    loadNotifs();
  }

  const navItems = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, color: "text-white" },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle, color: "text-amber-200" },
    { to: "/service-requests", label: "Service Requests", icon: ClipboardList, color: "text-sky-200" },
    { to: "/change-requests", label: "Change Requests", icon: GitPullRequest, color: "text-violet-200" },
    { to: "/problems", label: "Problems", icon: Wrench, color: "text-pink-200" },
    { to: "/knowledge-base", label: "Knowledge Base", icon: BookOpen, color: "text-teal-200" },
  ];
  if (hasRole("admin", "it_team", "it_manager", "it_security", "cfo"))
    navItems.push({ to: "/cab-calendar", label: "CAB Calendar", icon: Calendar, color: "text-indigo-200" });
  // v2.3 #1: IT team + IT Security also need to see what's overdue (they do the work)
  if (hasRole("admin", "it_manager", "it_team", "it_security"))
    navItems.push({ to: "/overdue", label: "Overdue Work", icon: Briefcase, color: "text-rose-200" });
  if (hasRole("admin", "it_manager"))
    navItems.push({ to: "/reports", label: "Reports", icon: BarChart3, color: "text-emerald-200" });
  if (hasRole("admin"))
    navItems.push({ to: "/admin", label: "Admin Panel", icon: Settings, color: "text-white/90" });

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* ============== Sidebar ============== */}
      <aside className="w-64 bg-sidebar-gradient text-white flex flex-col relative">
        {/* shimmer overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />

        <div className="relative px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
              <Sparkles size={18} className="text-emerald-200" />
            </div>
            <div>
              <div className="font-bold text-lg leading-tight">Hima Cement</div>
              <div className="text-emerald-200/80 text-xs tracking-wider uppercase">ITSM Portal</div>
            </div>
          </div>
        </div>

        <nav className="relative flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map(({ to, label, icon: Icon, color }) => (
            <NavLink key={to} to={to} end={to === "/"}
              className={({ isActive }) =>
                `nav-link ${isActive ? "nav-link-active" : ""}`
              }>
              <Icon size={17} className={color} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="relative px-3 py-3 border-t border-white/10 bg-black/10">
          <div className="px-3 py-2">
            <div className="text-sm font-semibold text-white">{user?.full_name}</div>
            <div className="text-xs text-emerald-100/70 mt-0.5 flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse-soft" />
              {user?.role} · {user?.plant || "—"}
            </div>
          </div>
          <button onClick={() => { logout(); nav("/"); }}
            className="w-full mt-1 nav-link text-rose-200 hover:text-white hover:bg-rose-500/30">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>

      {/* ============== Main ============== */}
      <main className="flex-1 flex flex-col">
        {/* Topbar with notifications */}
        <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 h-14 flex items-center justify-end px-6 gap-4 sticky top-0 z-30">
          <div className="relative">
            <button onClick={() => setOpen((o) => !o)}
              className="relative p-2 rounded-lg hover:bg-slate-100 transition">
              <Bell size={18} className="text-slate-700" />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-gradient-to-br from-rose-500 to-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ring-2 ring-white">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
            {open && (
              <div className="absolute right-0 mt-2 w-96 bg-white border border-slate-200 rounded-xl shadow-elevated overflow-hidden z-50 animate-fade-in">
                <div className="flex items-center justify-between p-3.5 border-b border-slate-100 bg-gradient-to-r from-brand-500 to-brand-600 text-white">
                  <span className="font-semibold">Notifications</span>
                  <button onClick={markAll} className="text-xs underline-offset-2 hover:underline">Mark all read</button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 && (
                    <div className="p-6 text-sm text-slate-500 text-center">No notifications yet.</div>
                  )}
                  {notifs.slice(0, 30).map((n) => (
                    <Link key={n.id} to={n.link || "/"} onClick={() => setOpen(false)}
                      className={`block p-3 border-b border-slate-100 hover:bg-slate-50 transition ${n.is_read ? "" : "bg-brand-50/30"}`}>
                      <div className="text-sm font-medium text-slate-800">{n.title}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{n.message}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="flex-1 p-6 overflow-y-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
